import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faClock, 
  faEdit, 
  faArrowLeft, 
  faUsers,
  faRupeeSign,
  faDownload,
  faSearch,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function ViewProgram() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingPending, setIsDeletingPending] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchProgramDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await adminService.getProgram(id);
      setProgram(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching program details:", err);
      setError("Failed to load program details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchParticipants = useCallback(async () => {
    try {
      setParticipantsLoading(true);
      const params = {
        page: currentPage,
        per_page: 10
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      const response = await adminService.getProgramParticipants(id, params);
      setParticipants(response.items);
      setTotalPages(response.pagination.total_pages);
      setParticipantsError(null);
    } catch (err) {
      console.error("Error fetching participants:", err);
      setParticipantsError("Failed to load participants. Please try again.");
    } finally {
      setParticipantsLoading(false);
    }
  }, [id, currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchProgramDetails();
  }, [fetchProgramDetails]);

  useEffect(() => {
    if (program) {
      fetchParticipants();
    }
  }, [program, fetchParticipants]);

  const handleDownloadParticipants = async () => {
    try {
      await adminService.downloadProgramParticipants(id);
    } catch (err) {
      console.error("Error downloading participants:", err);
      setParticipantsError("Failed to download participants list. Please try again.");
    }
  };

  const handleBulkDeletePending = async (type = 'all') => {
    try {
      setIsDeletingPending(true);
      setDeleteError(null);
      await adminService.deletePendingRegistrations(id, { type });
      await fetchProgramDetails();
      await fetchParticipants();
    } catch (err) {
      console.error('Error deleting pending registrations:', err);
      setDeleteError('Failed to delete pending registrations.');
    } finally {
      setIsDeletingPending(false);
    }
  };

  const handleDeleteSinglePending = async (participant) => {
    try {
      setIsDeletingPending(true);
      setDeleteError(null);
      const type = participant.is_member ? 'members' : 'non-members';
      await adminService.deleteSingleRegistration(id, participant.id, { type });
      await fetchProgramDetails();
      await fetchParticipants();
    } catch (err) {
      console.error('Error deleting registration:', err);
      setDeleteError('Failed to delete registration.');
    } finally {
      setIsDeletingPending(false);
    }
  };
  

  // Format date for display (UTC, no timezone conversion)
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    // Parse as UTC and format without timezone conversion
    const date = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = date.split('-');
    const dateObj = new Date(year, month - 1, day);
    const options = { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    };
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  };

  // Format time for display (UTC, no timezone conversion)
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    // Extract time part and display as is
    if (dateString.includes('T')) {
      const timePart = dateString.split('T')[1];
      const [hours, minutes] = timePart.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    }
    return dateString;
  };

  // Format date and time for participants (UTC, no timezone conversion)
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    // Parse date and time parts separately to avoid timezone conversion
    const [datePart, timePart] = dateString.includes('T') ? dateString.split('T') : [dateString, ''];
    const [year, month, day] = datePart.split('-');
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month, 10) - 1];
    
    let timeStr = '';
    if (timePart) {
      const [hours, minutes] = timePart.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      timeStr = ` ${hour12}:${minutes} ${ampm}`;
    }
    
    return `${day} ${monthName} ${year}${timeStr}`;
  };

  // Format currency (Indian Rupees) for display
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    // Ensure amount is a number
    const num = Number(amount);
    if (isNaN(num)) return 'N/A';
    // Format using en-IN locale and INR currency symbol
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
    } catch (e) {
      return `₹${num}`;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDeleteProgram = async () => {
    setIsDeleting(true);
    try {
      await adminService.deleteProgram(id);
      navigate('/admin/programs');
    } catch (err) {
      console.error("Error deleting program:", err);
      setError("Failed to delete program. Please try again.");
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading program details...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Program Details</h1>
          <ul className="admin-breadcrumb">
            <li className="admin-breadcrumb-item">
              <Link to="/admin/dashboard">Dashboard</Link>
            </li>
            <li className="admin-breadcrumb-item">
              <Link to="/admin/programs">Programs</Link>
            </li>
            <li className="admin-breadcrumb-item active">Program Details</li>
          </ul>
        </div>
        
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '0.25rem',
          marginBottom: '1rem' 
        }}>
          {error}
          <div style={{ marginTop: '1rem' }}>
            <button 
              className="admin-btn admin-btn-primary"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!program) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Program Not Found</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          The requested program could not be found.
          <div style={{ marginTop: '1rem' }}>
            <Link to="/admin/programs" className="admin-btn admin-btn-primary">
              Back to Programs
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Program Details</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/programs">Programs</Link>
          </li>
          <li className="admin-breadcrumb-item active">{program.title}</li>
        </ul>
      </div>
      
      <div className="admin-page-actions" style={{ marginBottom: '1rem' }}>
        <Link 
          to="/admin/programs"
          className="admin-btn admin-btn-light admin-btn-with-icon"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Programs</span>
        </Link>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link 
            to={`/admin/programs/${id}/edit`}
            className="admin-btn admin-btn-primary admin-btn-with-icon"
          >
            <FontAwesomeIcon icon={faEdit} />
            <span>Edit Program</span>
          </Link>
          
          <button 
            onClick={confirmDelete}
            className="admin-btn admin-btn-danger admin-btn-with-icon"
            disabled={isDeleting}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>Delete Program</span>
          </button>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">{program.title}</h2>
          <div>
            <span
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '2rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                backgroundColor: 
                  program.status === 'upcoming' ? '#cce5ff' : 
                  program.status === 'ongoing' ? '#d4edda' : 
                  program.status === 'completed' ? '#d1ecf1' : 
                  '#f8d7da',
                color: 
                  program.status === 'upcoming' ? '#004085' : 
                  program.status === 'ongoing' ? '#155724' : 
                  program.status === 'completed' ? '#0c5460' : 
                  '#721c24'
              }}
            >
              {program.status}
            </span>
          </div>
        </div>
        
        <div className="admin-card-body">
          <div className="admin-program-details"> 
            <div className="admin-program-detail-item">
              <FontAwesomeIcon icon={faCalendarAlt} className="admin-program-detail-icon" /> 
              <div>
                <h4>Event Date</h4>
                <div>{formatDate(program.program_date)}</div>
                {program.end_date && (
                  <div>to {formatDate(program.end_date)}</div>
                )}
              </div>
            </div>
            
            <div className="admin-program-detail-item">
              <FontAwesomeIcon icon={faClock} className="admin-program-detail-icon" />
              <div>
                <h4>Time</h4>
                <div>{formatTime(program.program_date)}</div>
                {program.end_date && (
                  <div>to {formatTime(program.end_date)}</div>
                )}
              </div>
            </div>
            
            <div className="admin-program-detail-item">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="admin-program-detail-icon" />
              <div>
                <h4>Location</h4>
                <div>{program.location}</div>
                {program.location_details && (
                  <div className="admin-program-detail-subtext">{program.location_details}</div>
                )}
              </div>
            </div>
            
            <div className="admin-program-detail-item">
              <FontAwesomeIcon icon={faUsers} className="admin-program-detail-icon" />
              <div>
                <h4>Capacity</h4>
                <div>{program.capacity ? `${program.capacity} participants` : 'Unlimited'}</div>
              </div>
            </div>
            
            {program.registration_fee > 0 && (
              <div className="admin-program-detail-item">
                <FontAwesomeIcon icon={faRupeeSign} className="admin-program-detail-icon" />
                <div>
                  <h4>Registration Fee</h4>
                  <div>₹{program.registration_fee}</div>
                </div>
              </div>
            )}
          </div>
          
          <div className="admin-program-description">
            <h3>Description</h3>
            <p>{program.description}</p>
          </div>
          
          <div className="admin-program-metadata">
            <div className="admin-program-metadata-item">
              <span className="admin-program-metadata-label">Program Type:</span>
              <span className="admin-program-metadata-value">{program.type}</span>
            </div>
            
            <div className="admin-program-metadata-item">
              <span className="admin-program-metadata-label">Featured:</span>
              <span className="admin-program-metadata-value">{program.featured ? 'Yes' : 'No'}</span>
            </div>
            
            <div className="admin-program-metadata-item">
              <span className="admin-program-metadata-label">Created:</span>
              <span className="admin-program-metadata-value">{formatDate(program.created_at)}</span>
            </div>
            
            <div className="admin-program-metadata-item">
              <span className="admin-program-metadata-label">Last Updated:</span>
              <span className="admin-program-metadata-value">{formatDate(program.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Participants Section */}
      <div className="admin-card" style={{ marginTop: '2rem' }}>
        <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="admin-card-title">
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.5rem' }} />
            Registered Participants
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="admin-btn admin-btn-outline admin-btn-with-icon"
              onClick={() => handleBulkDeletePending('all')}
              disabled={isDeletingPending}
              title="Delete all pending (members and non-members)"
            >
              <FontAwesomeIcon icon={faTrash} />
              <span>Delete Pending (All)</span>
            </button>
            <button 
              className="admin-btn admin-btn-outline"
              onClick={() => handleBulkDeletePending('members')}
              disabled={isDeletingPending}
              title="Delete pending members"
            >
              Pending Members
            </button>
            <button 
              className="admin-btn admin-btn-outline"
              onClick={() => handleBulkDeletePending('non-members')}
              disabled={isDeletingPending}
              title="Delete pending non-members"
            >
              Pending Non-Members
            </button>
            <button 
              className="admin-btn admin-btn-success admin-btn-with-icon"
              onClick={handleDownloadParticipants}
            >
              <FontAwesomeIcon icon={faDownload} />
              <span>Download List</span>
            </button>
          </div>
        </div>
        
        <div className="admin-card-body">
          {/* Search and Filters */}
            {deleteError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '0.75rem',
                borderRadius: '0.25rem',
                marginBottom: '1rem'
              }}>
                {deleteError}
              </div>
            )}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap', 
            gap: '1rem',
            alignItems: 'center'
          }}>
            <form 
              onSubmit={handleSearch} 
              style={{ 
                display: 'flex',
                maxWidth: '500px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                borderRadius: '4px'
              }}
            >
              <div style={{ 
                position: 'relative', 
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#ffffff',
                borderRadius: '4px 0 0 4px',
                paddingLeft: '10px',
                border: '1px solid #ced4da',
                borderRight: 'none'
              }}>
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    color: '#6c757d',
                    marginRight: '8px'
                  }} 
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email"
                  style={{
                    padding: '0.75rem 1rem',
                    border: 'none',
                    borderRadius: '4px 0 0 4px',
                    width: '100%',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '1rem',
                    color: '#495057'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6c757d',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ 
                  borderRadius: '0 4px 4px 0',
                  padding: '0.75rem 1.25rem',
                  border: 'none',
                  fontSize: '1rem'
                }}
              >
                Search
              </button>
            </form>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              padding: '0 15px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              border: '1px solid #ced4da'
            }}>
              <label 
                htmlFor="statusFilter" 
                style={{ 
                  marginRight: '0.5rem',
                  fontWeight: '500',
                  color: '#495057'
                }}
              >
                Status:
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{
                  padding: '0.65rem 0.75rem',
                  borderRadius: '0.25rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  outline: 'none',
                  cursor: 'pointer',
                  color: '#495057'
                }}
              >
                <option value="">All</option>
                <option value="registered">Registered</option>
                <option value="pending">Pending</option>
                <option value="registered">Registered</option>
                <option value="confirmed">Confirmed</option>
                <option value="attended">Attended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          
          {/* Participants Table */}
          {participantsLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading participants...</div>
          ) : participantsError ? (
            <div style={{ 
              backgroundColor: '#f8d7da',
              color: '#721c24', 
              padding: '1rem', 
              borderRadius: '0.25rem',
              marginBottom: '1rem' 
            }}>
              {participantsError}
            </div>
          ) : participants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              No participants found for this program.
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Sl No.</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Phone</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Registration Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Amount</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Payment</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant, index) => (
                      <tr key={`${participant.is_member ? 'member' : 'non-member'}-${participant.id}-${index}`} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.75rem' }}>{(currentPage - 1) * 10 + index + 1}</td>
                        <td style={{ padding: '0.75rem' }}>{participant.name}</td>
                        <td style={{ padding: '0.75rem' }}>{participant.email}</td>
                        <td style={{ padding: '0.75rem' }}>{participant.phone}</td>
                        <td style={{ padding: '0.75rem' }}>{formatDateTime(participant.registration_date)}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            backgroundColor: 
                              participant.status === 'confirmed' ? '#d4edda' : 
                              participant.status === 'registered' ? '#cce5ff' : 
                              participant.status === 'attended' ? '#d1ecf1' : 
                              '#f8d7da',
                            color: 
                              participant.status === 'confirmed' ? '#155724' : 
                              participant.status === 'registered' ? '#004085' : 
                              participant.status === 'attended' ? '#0c5460' : 
                              '#721c24'
                          }}>
                            {participant.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem' }}>{formatCurrency(participant.payment_amount)}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: 
                              participant.payment_status === 'completed' ? '#d4edda' : 
                              participant.payment_status === 'pending' ? '#fff3cd' : 
                              '#f8d7da',
                            color: 
                              participant.payment_status === 'completed' ? '#155724' : 
                              participant.payment_status === 'pending' ? '#856404' : 
                              '#721c24'
                          }}>
                            {participant.payment_status}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {participant.status === 'pending' ? (
                            <button
                              className="admin-btn admin-btn-danger"
                              onClick={() => handleDeleteSinglePending(participant)}
                              disabled={isDeletingPending}
                              title="Delete this pending registration"
                            >
                              Delete
                            </button>
                          ) : (
                            <span style={{ color: '#6c757d' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center', 
                  marginTop: '1.5rem',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="admin-btn admin-btn-light"
                  >
                    Previous
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="admin-btn admin-btn-light"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>Confirm Delete</h3>
              <button
                onClick={cancelDelete}
                className="admin-modal-close"
                disabled={isDeleting}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="admin-modal-body">
              <p>Are you sure you want to delete this program? This action cannot be undone.</p>
              <p><strong>Program:</strong> {program?.title}</p>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={cancelDelete}
                className="admin-btn admin-btn-light"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProgram}
                className="admin-btn admin-btn-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Program'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ViewProgram;
